export type NiceScaleResult = {
  range: number;
  rangeDiff: number;
  niceMin: number;
  niceMax: number;
  tickAmount: number;
  tickInterval: number;
  ticks: number[];
};

export default function niceScale(
  bounds: [number, number],
  options?: {
    ticks?: number | null;
    minTicks?: number;
    maxTicks?: number;
  },
): NiceScaleResult {
  const ticks = options?.ticks ?? null;
  const minTicks = ticks ?? options?.minTicks ?? 1;
  const maxTicks = ticks ?? options?.maxTicks ?? 10;

  let [min, max] = bounds;
  if (max < min) [max, min] = bounds;

  if (max === min)
    return {
      range: 0,
      rangeDiff: 0,
      niceMin: 0,
      niceMax: 0,
      tickAmount: 0,
      tickInterval: 0,
      ticks: [],
    };

  const range = max - min;

  const results: NiceScaleResult[] = [];
  let bestResult: NiceScaleResult | null = null;

  for (let tickAmount = maxTicks; tickAmount >= minTicks; tickAmount--) {
    let tickInterval = niceTickInterval(range / tickAmount, "up");
    let newRange = tickInterval * tickAmount;

    let niceMin = min;
    let niceMax = min + newRange;

    if (tickAmount == 1) {
      niceMin = Math.floor(min / tickInterval) * tickInterval;
      niceMax = Math.ceil(max / tickInterval) * tickInterval;

      newRange = niceMax - niceMin;
      tickInterval = newRange;
    } else {
      while (true) {
        const desiredNiceMin = Math.floor(min / tickInterval) * tickInterval;

        if (niceMin === desiredNiceMin && niceMax >= max) break;

        tickInterval = niceTickInterval(
          (max - desiredNiceMin) / tickAmount,
          "up",
        );
        newRange = tickInterval * tickAmount;

        niceMin = Math.floor(min / tickInterval) * tickInterval;
        niceMax = niceMin + newRange;
      }
    }

    const result: NiceScaleResult = {
      range: newRange,
      rangeDiff: newRange - range,
      niceMin,
      niceMax,
      tickAmount,
      tickInterval,
      ticks: [],
    };

    for (let i = 0; i <= tickAmount; i++) {
      result.ticks.push(niceMin + tickInterval * i);
    }

    results.push(result);

    if (!bestResult || result.rangeDiff < bestResult.rangeDiff) {
      bestResult = result;
    }
  }

  return bestResult ?? results[0];
}

export function niceTickInterval(
  number: number,
  round: "up" | "down" | "nearest",
) {
  const exponent = Math.floor(Math.log10(number));
  const fraction = number / Math.pow(10, exponent);
  const niceFraction = niceDecimal(fraction / 10, round) * 10;
  return toPrecision(niceFraction * Math.pow(10, exponent));
}

export function niceDecimal(decimal: number, round: "up" | "down" | "nearest") {
  const stepSizes = [0, 0.1, 0.2, 0.25, 0.5, 1];

  switch (round) {
    case "up": {
      let prevStepSize = 1;
      for (const stepSize of [...stepSizes].reverse()) {
        if (decimal > stepSize) return prevStepSize;
        prevStepSize = stepSize;
      }
      return 0;
    }

    case "down": {
      let prevStepSize = 0;
      for (const stepSize of stepSizes) {
        if (decimal < stepSize) return prevStepSize;
        prevStepSize = stepSize;
      }
      return 1;
    }

    case "nearest": {
      let prevStepSize = 0;
      for (const stepSize of stepSizes) {
        if (decimal === stepSize) return stepSize;
        if (decimal < stepSize) {
          const midpoint = prevStepSize + (stepSize - prevStepSize) / 2; //?
          return decimal <= midpoint ? prevStepSize : stepSize;
        }
        prevStepSize = stepSize;
      }
      return 1;
    }
  }
}

function toPrecision(number: number, precision = 7) {
  const factor = Math.pow(10, precision);
  return Math.round(number * factor) / factor;
}
